import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { SharedResourcesData } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { FormTextField } from '../../formComponents/FormTextField';
import { getCellInlinePadding } from '../../../table/cellInlinePadding';

type SharedResourcesAllocationTableProps = {
  values: {
    resource_id: string;
    unit_capacity?: number;
    total_units?: number;
  }[];
  onChange: (
    allocations: {
      resource_id: string;
      unit_capacity?: number;
      total_units?: number;
    }[],
  ) => void;
  resources?: SharedResourcesData;
  disabled: boolean;
};

export const SharedResourcesAllocationTable = ({
  values,
  onChange,
  resources,
  disabled,
}: SharedResourcesAllocationTableProps) => {
  const language = useLanguage();
  const { t } = useTranslation();
  if (!resources) {
    return null;
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={getCellInlinePadding(0, 4)}>{t('sharedResources.resource')}</TableCell>
            <TableCell sx={getCellInlinePadding(1, 4)}>{t('sharedResources.totalUnits')}</TableCell>
            <TableCell sx={getCellInlinePadding(2, 4)}>{t('sharedResources.unitCapacity')}</TableCell>
            <TableCell sx={getCellInlinePadding(3, 4, true)}>{t('sharedResources.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {values.map((allocation, index) => (
            <TableRow key={`${allocation.resource_id}-${index}`}>
              <TableCell sx={getCellInlinePadding(0, 4)}>
                <Select
                  size="small"
                  value={allocation.resource_id}
                  onChange={(e) => {
                    const newAllocations = [...values];
                    newAllocations[index] = {
                      ...newAllocations[index],
                      resource_id: e.target.value,
                    };
                    onChange(newAllocations);
                  }}
                  disabled={disabled}>
                  {resources.automatic_dividable_resources?.map((resource) => (
                    <MenuItem key={resource.id} value={resource.id}>
                      {resource.label[language]}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>

              <TableCell sx={getCellInlinePadding(1, 4)}>
                <FormTextField
                  type="number"
                  fieldName={`shared_resources_allocations.automatic_dividable_resources.${index}.total_units`}
                  onChange={(e) => {
                    const newAllocations = [...values];
                    newAllocations[index] = {
                      ...newAllocations[index],
                      total_units: Number(e.target.value),
                    };
                    onChange(newAllocations);
                  }}
                  min={1}
                  disabled={disabled}
                  isFieldUpdate
                  placeholder={t('sharedResources.unitCapacityPlaceholderMax', {
                    max: resources.automatic_dividable_resources?.find((r) => r.id === allocation.resource_id)
                      ?.total_units,
                  })}
                />
              </TableCell>
              <TableCell sx={getCellInlinePadding(2, 4)}>
                <FormTextField
                  placeholder={t('sharedResources.unitCapacityPlaceholderMax', {
                    max: resources.automatic_dividable_resources?.find((r) => r.id === allocation.resource_id)
                      ?.unit_capacity,
                  })}
                  type="number"
                  fieldName={`shared_resources_allocations.automatic_dividable_resources.${index}.unit_capacity`}
                  onChange={(e) => {
                    const newAllocations = [...values];
                    newAllocations[index] = {
                      ...newAllocations[index],
                      unit_capacity: Number(e.target.value),
                    };
                    onChange(newAllocations);
                  }}
                  disabled={disabled}
                  min={1}
                  isFieldUpdate
                />
              </TableCell>
              <TableCell sx={{ ...getCellInlinePadding(3, 4, true), textAlign: 'end' }}>
                <IconButton onClick={() => onChange(values.filter((_, i) => i !== index))} disabled={disabled}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {values.length < (resources.automatic_dividable_resources?.length || 0) && (
        <Button
          startIcon={<Add />}
          onClick={() => onChange([...values, { resource_id: '' }])}
          disabled={disabled}
          sx={{ m: 2 }}>
          {t('sharedResources.addAllocation')}
        </Button>
      )}
    </TableContainer>
  );
};
